
import TUIViewer from "../../components/application/TUIViewer.vue";
import { ComputedRef, defineComponent, watch } from "vue";
import { useStore } from "vuex";
import { Alternative, ExerciseStudent, Question } from "./helpers";
import { computed } from "@vue/reactivity";
import { questionErrorHandler } from "./questionnaire_error_handler";
import { useRouter } from "vue-router";
import { scrollParentToElement } from "../../utils/scroll";
import { dispatchError } from "../../utils/error_handling";

export default defineComponent({
  name: "QuestionAlternatives",
  components: { TUIViewer },
  setup() {
    const store = useStore();
    const router = useRouter();

    const question: ComputedRef<Question> = computed(
      () => store.getters["content_module/questionnaire/currentQuestion"]
    );

    const alternatives: ComputedRef<Alternative[]> = computed(
      () => store.getters["content_module/questionnaire/alternatives"]
    );

    watch(question, () => onCurrentQuestionUpdate());

    const onCurrentQuestionUpdate = async (): Promise<void> => {
      try {
        await reloadQuestionControls();
        await updateQuestionPageOnURL();
        await shuffleAlternatives();
      } catch (e) {
        console.error("[onCurrentQuestionUpdate] Error ", e);
        await questionErrorHandler(e);
      }
    };

    const reloadQuestionControls = async (): Promise<void> => {
      try {
        await store.dispatch(
          "content_module/questionnaire/questionnaire_control/reload"
        );
      } catch (e) {
        console.error("[reloadQuestionControls] Error ", e);
        throw e;
      }
    };

    const updateQuestionPageOnURL = async (): Promise<void> => {
      try {
        const currentRoute = router.currentRoute.value;
        if (!currentRoute) return;
        await router.push({
          name: currentRoute.name as string,
          params: { ...currentRoute.params, page: question.value.page }
        });
      } catch (e) {
        console.error("[reloadQuestionControls] Error ", e);
        throw e;
      }
    };

    const shuffleAlternatives = async (): Promise<void> => {
      try {
        await store.dispatch(
          "content_module/questionnaire/shuffleAlternatives"
        );
      } catch (e) {
        console.error("[shuffleAlternatives] Error ", e);
        throw e;
      }
    };

    const { isAbleToRedoQuestions }: ExerciseStudent = store.getters[
      "content_module/exercise/exercise"
    ];

    const onSelectAlternative = async (
      alternative: Alternative
    ): Promise<void> => {
      try {
        if (!isAbleToRedoQuestions && question.value.status.attempts > 0)
          return;
        if (
          question.value.attemptLimit !== null &&
          question.value.status.attempts === question.value.attemptLimit
        ) {
          return;
        }
        await replyQuestion(alternative);
        scrollToFeedbackBanner();
        await shuffleAlternatives();
        /** TODO: SHOULD NOT USE THIS. */
        await loadAllContents();
      } catch (e) {
        console.error("[onSelectAlternative] Error ", e);
        await questionErrorHandler(e);
      }
    };

    const replyQuestion = async (alternative: Alternative): Promise<void> => {
      try {
        const payload = {
          page: question.value.page,
          alternativeID: alternative.id
        };
        await store.dispatch(
          "content_module/questionnaire/replyQuestionByPage",
          payload
        );
      } catch (e) {
        console.error("[replyQuestion] Error ", e);
        throw e;
      }
    };

    const loadAllContents = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/loadAllContents");
      } catch (e) {
        console.error("[loadContents] Error dispatch ", e);
        await dispatchError(e);
      }
    };

    const scrollToFeedbackBanner = () => {
      const parentElement: HTMLElement | null = document.querySelector(
        "[data-questionnaire-view]"
      );
      const element: HTMLElement | null = document.querySelector(
        "[data-feedback]"
      );
      if (!parentElement || !element) {
        return;
      }
      scrollParentToElement(parentElement, element, 500);
    };

    return {
      question,
      alternatives,
      isAbleToRedoQuestions,
      onSelectAlternative
    };
  }
});
